import { useState, type ReactNode } from "react";

import { useApiKeys, useCreateApiKey, useDeleteApiKey } from "../../api/queries";
import type { ApiKey } from "../../api/types";
import { Button } from "../../ui/Button";

/** Served by the client build from `docs/API-ACCESS.md` (see `vite.config.ts`). */
export const API_GUIDE_PATH = "/api-guide.md";

/**
 * API keys. The full secret is returned only by `apiKeys.create` (note 01 §4.5),
 * so it is shown once, in a panel the user must dismiss, and never re-fetched.
 */
export function ApiKeysScreen(): ReactNode {
  const keys = useApiKeys();
  const create = useCreateApiKey();
  const remove = useDeleteApiKey();
  const [name, setName] = useState("");
  const [issued, setIssued] = useState<{ name: string; value: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const secretOf = (key: ApiKey): string | undefined => key.value ?? undefined;

  return (
    <section className="max-w-2xl">
      <h2 className="text-xl font-semibold">API keys</h2>
      <p className="mt-1 text-sm text-muted">
        Keys act as you: they carry your role and your permissions.
      </p>
      <p className="mt-2 text-sm text-muted">
        Connecting an AI agent or a script? Give it the{" "}
        <a href={API_GUIDE_PATH} target="_blank" rel="noreferrer" className="text-accent underline">
          API guide
        </a>{" "}
        along with the key: it explains the endpoints, the markdown dialect, uploads, imports and
        rate limits.
      </p>

      {issued !== null && (
        <div role="status" className="mt-5 rounded-md border border-accent bg-surface p-4 text-sm">
          <p className="font-medium">Copy this value now — it is not shown again.</p>
          <code className="mt-2 block overflow-x-auto rounded bg-canvas px-3 py-2 font-mono text-xs">
            {issued.value}
          </code>
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(issued.value).then(
                  () => {
                    setCopied(true);
                  },
                  () => {
                    setCopied(false);
                  },
                );
              }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIssued(null);
                setCopied(false);
              }}
            >
              Done
            </Button>
          </div>
        </div>
      )}

      <form
        aria-label="Create API key"
        className="mt-5 flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (trimmed === "") return;
          create.mutate(
            { name: trimmed },
            {
              onSuccess: (key) => {
                setName("");
                const secret = secretOf(key);
                if (secret !== undefined) setIssued({ name: key.name, value: secret });
              },
            },
          );
        }}
      >
        <label className="flex flex-1 flex-col gap-1 text-sm">
          Key name
          <input
            name="keyName"
            value={name}
            required
            onChange={(event) => {
              setName(event.target.value);
            }}
            className="rounded-md border border-line bg-raised px-3 py-2 text-sm"
          />
        </label>
        <Button type="submit" variant="primary" disabled={create.isPending}>
          {create.isPending ? "Creating…" : "Create key"}
        </Button>
      </form>

      {create.isError && (
        <p role="alert" className="mt-2 text-sm text-danger">
          {create.error.message}
        </p>
      )}

      <h3 className="mt-8 text-sm font-semibold">Existing keys</h3>
      {keys.isPending ? (
        <p className="mt-2 text-sm text-muted" role="status">
          Loading keys…
        </p>
      ) : keys.isError ? (
        <p className="mt-2 text-sm text-danger" role="alert">
          {keys.error.message}
        </p>
      ) : (keys.data ?? []).length === 0 ? (
        <p className="mt-2 text-sm text-muted">No keys yet.</p>
      ) : (
        <ul
          aria-label="API keys"
          className="mt-2 divide-y divide-line rounded-md border border-line"
        >
          {(keys.data ?? []).map((key) => (
            <li key={key.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{key.name}</p>
                {key.last4 !== undefined && (
                  <p className="font-mono text-xs text-muted">…{key.last4}</p>
                )}
              </div>
              <Button
                size="sm"
                variant="danger"
                aria-label={`Revoke ${key.name}`}
                disabled={remove.isPending}
                onClick={() => {
                  remove.mutate({ id: key.id });
                }}
              >
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
